import { MetaFunction, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, useLocation } from "@remix-run/react";
import { getCountryName, getFormattedCountryName } from "~/lib/helpers/constants";
import HomeServices from "~/lib/services/HomeServices";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import { defer } from "@remix-run/node";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDeviceInfo } from "~/lib/hooks/useViewportListener";
import DesktopLayout from "~/components/blocks/Campaign_Page/DesktopLayout";
import MobileLayout from "~/components/blocks/Campaign_Page/MobileLayout";
import AreaServices from "~/lib/services/AreaServices";
import ApiServiceV2 from "~/lib/services/GeoSearchServices";
import PropertiesService from "~/lib/services/PropertiesService";
import { GetDomainName } from "~/helpers/UrlHelper";
import { LEAD_FORMS, trackFormOpen, trackFormStep } from "~/lib/ConversionTracking";

function toQueryString(obj: Record<string, string | number | boolean | null | undefined>) {
    return (
        Object.entries(obj)
            .filter(([, value]) => {
                if (value === null || value === undefined) return false;
                if (Array.isArray(value) && value.length === 0) return false;
                if (typeof value === 'string' && value.trim() === '') return false;
                return true;
            })
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join('&')
    );
}

function parseQueryString(queryString: string): Record<string, string | number> {
    const params = new URLSearchParams(queryString);
    const result: Record<string, string | number> = {};
    params.forEach((value, key) => {
        result[key] = isNaN(Number(value)) ? value : Number(value);
    });
    return result;
}

const formatCity = (city: string) => {
    if (!city) return "";
    return city
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatState = (state: string) => {
    if (!state) return "";
    if (state.length <= 3) {
        return state.toUpperCase();
    }
    return state
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const meta: MetaFunction = ({ data, location }) => {
    const { countryName, country } = data;
    const { pathname } = location;
    const baseUrl = `${GetDomainName(country)}`;
    const canonicalUrl = `${baseUrl}${pathname}`;

    return [
        { title: `Office Spaces | Find Your Perfect Workspace in ${countryName}` },
        {
            name: "description",
            content: `Discover flexible office spaces and coworking solutions tailored to your business needs. Compare prices and amenities in top locations in ${countryName}.`,
        },
        {
            name: "robots",
            content: "noindex, follow",
        },
        {
            tagName: "link",
            rel: "canonical",
            href: canonicalUrl,
        },
    ];
};

export async function loader({ params, request }: LoaderFunctionArgs) {
    const { isMobile } = getDeviceInfo(request, 768);
    const url = new URL(request.url);
    const country = params.country || DEFAULT_REGION;
    const city = url.searchParams.get("city") || "";
    const state = url.searchParams.get("state") || "";
    const workspaceType = url.searchParams.get("workspaceType") || "All";
    const latitude = url.searchParams.get("latitude") || "";
    const longitude = url.searchParams.get("longitude") || "";
    const faqs = AreaServices.getFAQsParentPage("workspace", getCountryName(country));

    // Fetch home summary for social proof and rating
    const homeSummary = HomeServices.getHomeSummary(country);

    // Build geo search query for the campaign page listings
    const geoSearchParams: Record<string, string> = {
        country: getCountryName(country),
        city: formatCity(city),
        perPage: "8",
    };

    if (workspaceType && workspaceType !== "All") {
        geoSearchParams.workspaceType = workspaceType;
    }

    if (state) {
        geoSearchParams.state = formatState(state);
    }

    if (latitude) {
        geoSearchParams.latitude = latitude;
    }

    if (longitude) {
        geoSearchParams.longitude = longitude;
    }

    const geoQuery = Object.entries(geoSearchParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");

    // Fetch geo search data (range fetched client-side like AreaPage)
    const geoData = await PropertiesService.geoSearch(geoQuery);


    return defer({
        isMobile,
        country,
        city,
        workspaceType,
        homeSummary,
        countryName: getFormattedCountryName(country, true),
        faqs,
        geoData,
        geoQuery,
        latitude,
        longitude,
        state,
    });
}

export default function CampaignPage() {
    const {
        isMobile, country, city, homeSummary, countryName, faqs,
        geoData: loaderGeoData, geoQuery: loaderGeoQuery,
        latitude, longitude, state: loaderState, workspaceType
    } = useLoaderData<typeof loader>();

    const location = useLocation();
    const { pathname } = location;

    const [step, setStep] = useState(1);

    // The hero form is the page, so mounting it is the open. Tracking the step
    // here rather than on the Continue button means a step added later is
    // covered without touching a button — the same wiring the quote pop-up uses.
    useEffect(() => {
        trackFormOpen(LEAD_FORMS.CAMPAIGN_HERO);
    }, []);

    useEffect(() => {
        trackFormStep({ form: LEAD_FORMS.CAMPAIGN_HERO, step });
    }, [step]);

    const handleContinue = () => {
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    // ========================================
    // GEO SEARCH STATE & LOGIC (mirrored from AreaPage)
    // ========================================
    const defaultFilters = useMemo(() => ({
        price: [],
        deskRange: [],
        officeTypes: [],
        amenities: [],
        sale: null,
        promo: "",
        minPeople: "",
        maxPeople: "",
        minPrice: "",
        maxPrice: "",
        workspaceType: "",
        page: 1,
    }), []);

    const [filterData, setFilterData] = useState<any>({
        ...defaultFilters,
        latitude: latitude || "",
        longitude: longitude || "",
        state: loaderState || "",
        officeTypes: workspaceType && workspaceType !== "All" ? workspaceType.split(",") : [],
        workspaceType: workspaceType && workspaceType !== "All" ? workspaceType : "",
    });
    const [geoQuery, setGeoQuery] = useState(() => loaderGeoQuery);
    const [geoData, setGeoData] = useState(() => loaderGeoData);
    const [selectedSort, setSelectedSort] = useState({ label: '', value: '' });
    const [range, setRange] = useState<any>(null);
    const [limit, setLimit] = useState(8);
    const [loading, setLoading] = useState(false);


    const handleLoadMore = () => {
        const nextLimit = limit + 8;
        setLimit(nextLimit);
        callGeoSearch({ perPage: nextLimit });
    };

    // Sync geo data when loader data changes (e.g., navigating to different country)
    useEffect(() => {
        setGeoData(loaderGeoData);
    }, [loaderGeoData]);

    useEffect(() => {
        setGeoQuery(loaderGeoQuery);
    }, [loaderGeoQuery]);

    // Fetch max price range client-side (same pattern as AreaPage)
    useEffect(() => {
        const fetchRange = async () => {
            try {
                const rangeData = await ApiServiceV2.getCountryMaxPrice(
                    `?country=${getCountryName(country)}&city=${city}`
                );
                setRange(rangeData);
            } catch (e) {
                // Silent fail
            }
        };
        fetchRange();
    }, [country, city]);

    // Function to change pages


    const callGeoSearch = async (options: { [x: string]: any }) => {
        setLoading(true);
        try {
            let newFilterData = {
                price: [],
                deskRange: [],
                sale: '',
                officeTypes: [],
                amenities: []
            };
            if (options.reset) {
                setFilterData(newFilterData);
                setLimit(8);
                setGeoQuery(loaderGeoQuery);
                const res = await PropertiesService.geoSearch(loaderGeoQuery);
                setGeoData(res);
            } else {
                // Keep empty string "" and 0, but skip null/undefined
                const cleanedOptions = Object.fromEntries(
                    Object.entries(options).filter(([_, v]) => v !== null && v !== undefined)
                );

                const state = { ...filterData, ...cleanedOptions };

                if (cleanedOptions.workspaceType !== undefined) {
                    state.officeTypes = cleanedOptions.workspaceType ? String(cleanedOptions.workspaceType).split(",") : [];
                }


                if ((cleanedOptions.minPrice !== undefined && cleanedOptions.minPrice !== null) || (cleanedOptions.maxPrice !== undefined && cleanedOptions.maxPrice !== null)) {
                    state.price = [
                        parseInt(String(cleanedOptions.minPrice || state.minPrice || 0)),
                        parseInt(String(cleanedOptions.maxPrice || state.maxPrice || 0))
                    ];
                }

                if (cleanedOptions.minPeople !== undefined && cleanedOptions.minPeople !== null) {
                    state.deskRange = [parseInt(String(cleanedOptions.minPeople)), parseInt(String(cleanedOptions.maxPeople))];
                }

                setFilterData(state);

                const { price, deskRange, country: _country, officeTypes, ...queryState } = state;

                const existingParams = parseQueryString(geoQuery);

                const finalQueryObj: any = {
                    ...existingParams,
                    country: getCountryName(options.country || country),
                    ...queryState,
                    perPage: options.perPage || limit,
                };

                if (finalQueryObj.city) {
                    finalQueryObj.city = formatCity(finalQueryObj.city);
                }
                if (finalQueryObj.state) {
                    finalQueryObj.state = formatState(finalQueryObj.state);
                }

                const query = toQueryString(finalQueryObj);

                setGeoQuery(query);

                const res = await PropertiesService.geoSearch(query);
                setGeoData(res);
            }
        } finally {
            setLoading(false);
        }
    };

    // Drawer function - updates state only, doesn't call API immediately
    const callGeoSearchForDrawer = useCallback((options: { [x: string]: string }) => {
        if (options.reset) {
            setFilterData({ ...defaultFilters });
            callGeoSearch({ reset: true, country: country });
            return;
        }

        setFilterData((prev: any) => {
            const next = { ...prev };

            if (options.workspaceType !== undefined) {
                const officeTypes = options.workspaceType.split(",").filter(Boolean);
                next.workspaceType = options.workspaceType;
                next.officeTypes = officeTypes.length ? officeTypes : [];
                return next;
            }

            if (options.amenities !== undefined) {
                const amenities = options.amenities.split(",").filter(Boolean);
                next.amenities = amenities;
                return next;
            }

            if (options.minPrice !== undefined) {
                next.minPrice = options.minPrice;
            }
            if (options.maxPrice !== undefined) {
                next.maxPrice = options.maxPrice;
            }
            if (options.minPrice !== undefined || options.maxPrice !== undefined) {
                const min = options.minPrice ? parseInt(options.minPrice, 10) : (prev.price?.[0] || 0);
                const max = options.maxPrice ? parseInt(options.maxPrice, 10) : (prev.price?.[1] || 0);
                next.price = [min, max];
            }

            if (options.minPeople !== undefined) {
                next.minPeople = options.minPeople;
            }
            if (options.maxPeople !== undefined) {
                next.maxPeople = options.maxPeople;
            }
            if (options.minPeople !== undefined || options.maxPeople !== undefined) {
                const min = options.minPeople ? parseInt(options.minPeople, 10) : (prev.deskRange?.[0] || 0);
                const max = options.maxPeople ? parseInt(options.maxPeople, 10) : (prev.deskRange?.[1] || 0);
                next.deskRange = [min, max];
            }

            if (options.promo !== undefined) {
                next.promo = options.promo;
            }
            if (options.sale !== undefined) {
                next.sale = options.sale;
            }

            Object.entries(options).forEach(([key, value]) => {
                if (![
                    "workspaceType",
                    "amenities",
                    "minPrice",
                    "maxPrice",
                    "minPeople",
                    "maxPeople",
                    "promo",
                    "sale",
                ].includes(key)) {
                    next[key] = value;
                }
            });

            return next;
        });
    }, [defaultFilters]);

    // Handle drawer apply - calls API with current filter state
    const handleDrawerApply = async () => {
        const options: Record<string, any> = {};

        if (filterData.minPeople !== undefined && filterData.minPeople !== null && filterData.minPeople !== "") {
            options.minPeople = filterData.minPeople;
        }
        if (filterData.maxPeople !== undefined && filterData.maxPeople !== null && filterData.maxPeople !== "") {
            options.maxPeople = filterData.maxPeople;
        }
        if (filterData.minPrice !== undefined && filterData.minPrice !== null && filterData.minPrice !== "") {
            options.minPrice = filterData.minPrice;
        }
        if (filterData.maxPrice !== undefined && filterData.maxPrice !== null && filterData.maxPrice !== "") {
            options.maxPrice = filterData.maxPrice;
        }
        if (filterData.officeTypes && filterData.officeTypes.length > 0) {
            options.workspaceType = filterData.officeTypes.join(",");
        }
        if (filterData.amenities && filterData.amenities.length > 0) {
            options.amenities = filterData.amenities.join(",");
        }
        if (filterData.promo) {
            options.promo = filterData.promo;
        }
        if (filterData.sale) {
            options.sale = filterData.sale;
        }
        if (filterData.latitude) {
            options.latitude = filterData.latitude;
        }
        if (filterData.longitude) {
            options.longitude = filterData.longitude;
        }
        if (filterData.state) {
            options.state = filterData.state;
        }

        // Call the actual API
        await callGeoSearch(options);
    };

    const layoutProps = {
        countryName,
        region: country,
        loaderData: { homeSummary },
        city,
        onContinue: handleContinue,
        onBack: handleBack,
        step,
        isMobile,
        faqs,
        // Geo search props
        query: geoQuery,
        filterData,
        callGeoSearch,
        callGeoSearchForDrawer,
        onDrawerApply: handleDrawerApply,
        setFilterData,
        geoData: { data: geoData, params: { country } },
        selectedSort,
        setSelectedSort,
        pathname,
        range,
        onLoadMore: handleLoadMore,
        hasMore: (geoData?.totalProperties || geoData?.data?.totalProperties || geoData?.totalListings || geoData?.data?.totalListings || 0) > (geoData?.visibleProperties || geoData?.data?.visibleProperties || limit),
        loading,
    };

    return (
        <div className="min-h-screen">
            {isMobile ? (
                <MobileLayout {...layoutProps} />
            ) : (
                <DesktopLayout {...layoutProps} />
            )}
        </div>
    );
}
